import { cloneDeep } from 'lodash';
import { Modal } from '@meitu/saas-ui';
import { fetchSvgContentWithCache } from '@meitu/saas-editor-core/es/utils/file-kit';
import { uploadFileToObs } from '@meitu/saas-editor-core/es/utils/upload-kit';
import { IAdvancedTextMaterial, ITextLayerProps } from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import { getUseCounts, transformToTextContents } from '@/editor/panel/views/text-art-panel/utils/text-art-opera';
import { defaultTextMark, getSvgDefaultData } from '@/editor/panel/views/text-mark-panel/utils/text-mark-opera';
import { editor } from '@/instance';
import { getFontMaterialModel } from '@/store/models/font-material-model';
import { TextMarkStruct } from '@/types/text-mark';

export class TextMarkSvgUpload {
    static modalInstance: any;
    static callback?: ((data: TextMarkStruct) => void) | ((data: IAdvancedTextMaterial) => void);
    static type: string;
    static textLayerId: string;

    static upload() {
        const inputEl = document.createElement('input');
        inputEl.type = 'file';
        inputEl.accept = 'image/svg+xml';
        inputEl.style.display = 'none';

        inputEl.onchange = async (event: Event) => {
            const files = (event.target as HTMLInputElement).files || [];

            if (files.length > 0) {
                const { url } = await uploadFileToObs(files[0]);

                if (TextMarkSvgUpload.type === 'textMark') {
                    // 划重点处理
                    const materialId = Math.random();

                    const markItem = {
                        animate: 0,
                        data: { ...cloneDeep(defaultTextMark), src: url },
                        materialId: `${materialId}`,
                    };
                    await fetchSvgContentWithCache(url);
                    const { defaultColor, strokeWidth, enableSize } = getSvgDefaultData(url);
                    markItem.data.defaultColor = defaultColor ?? '';

                    if (enableSize) {
                        markItem.data.enableSize = true;
                        markItem.data.sizeRange = [
                            Math.max(1, Math.ceil(strokeWidth / 2)),
                            strokeWidth,
                            strokeWidth * 2,
                        ];
                    }
                    const fontMaterialModel = getFontMaterialModel();
                    const highlights = [
                        ...fontMaterialModel.highlights.filter((item) => item.id !== markItem.materialId),
                        {
                            id: markItem.materialId,
                            data: markItem.data,
                            animate: markItem.animate,
                        },
                    ];
                    fontMaterialModel.setHighlights(highlights);
                    editor.services.textHighlight.setHighlights(highlights);
                    TextMarkSvgUpload.callback?.(markItem as TextMarkStruct & IAdvancedTextMaterial);
                } else if (TextMarkSvgUpload.type === 'textArt') {
                    // 文字特效处理
                    const materialId = Math.random();
                    const textLayer = editor.layer.getLayer(TextMarkSvgUpload.textLayerId);
                    const newLayer = cloneDeep(textLayer);

                    // }
                    // 加载 SVG 资源
                    await fetchSvgContentWithCache(url);

                    // 默认循环个数
                    const useCount = getUseCounts(url);
                    const textContents = transformToTextContents(newLayer).slice(0, useCount);

                    (newLayer.data as ITextLayerProps).text = (newLayer.data as ITextLayerProps).text;

                    editor.services.frame.addOrUpdateLayer(cloneDeep(newLayer));

                    const advancedTextMaterial = {
                        id: materialId.toString(),
                        material: {
                            defaultFontSize: 80,
                            defaultLetterSpacing: 0,
                            src: url,
                            useCount,
                        },
                        textContents,
                    };

                    TextMarkSvgUpload.callback?.(advancedTextMaterial as TextMarkStruct & IAdvancedTextMaterial);
                }
                TextMarkSvgUpload.modalInstance.destroy();
            }

            inputEl.remove();
        };

        document.body.appendChild(inputEl);
        inputEl.click();
    }

    static show(
        type: 'textMark',
        textLayerId: string,
        callback?: (data: TextMarkStruct) => void,
        canClose?: boolean,
    ): void;
    static show(
        type: 'textArt',
        textLayerId: string,
        callback?: (data: IAdvancedTextMaterial) => void,
        canClose?: boolean,
    ): void;
    static show(
        type: 'textMark' | 'textArt',
        textLayerId: string,
        callback?: ((data: TextMarkStruct) => void) | ((data: IAdvancedTextMaterial) => void),
        canClose = false,
    ) {
        TextMarkSvgUpload.type = type;
        TextMarkSvgUpload.textLayerId = textLayerId;
        TextMarkSvgUpload.callback = callback;

        TextMarkSvgUpload.modalInstance = Modal.confirm({
            title: '上传 SVG 文件',
            centered: true,
            closable: canClose,
            maskClosable: canClose,
            keyboard: false,
            hideIcon: true,
            hideCancelButton: true,
            hideOkButton: true,
            content: (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        cursor: 'pointer',
                        padding: 16,
                    }}
                    onClick={this.upload}
                >
                    <IconFont name="UploadBold" fontSize={18} color="var(--content-system-primary, #1C1D1F)" />
                    <span>点击上传</span>
                </div>
            ),
        });
    }
}
